const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Helper function to get auth token
const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

// Helper function to make API requests
const apiRequest = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<any> => {
  const token = getAuthToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(options.headers as HeadersInit),
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'API request failed');
    }

    return data;
  } catch (error: any) {
    console.error('API Error:', error);
    throw error;
  }
};

// Auth API
export const authApi = {
  // Auto login/register with Telegram ID
  loginWithTelegram: async (telegramData: {
    telegramId: string | number;
    firstName?: string;
    lastName?: string;
    username?: string;
  }) => {
    const response = await apiRequest('/auth/telegram', {
      method: 'POST',
      body: JSON.stringify({
        telegramId: String(telegramData.telegramId),
        firstName: telegramData.firstName || '',
        lastName: telegramData.lastName || '',
        username: telegramData.username || '',
      }),
    });

    if (response.success && response.token) {
      localStorage.setItem('authToken', response.token);
    }

    return response;
  },

  // Verify token
  verifyToken: async () => {
    return apiRequest('/auth/verify');
  },
};

// User API
export const userApi = {
  getProfile: async () => {
    const response = await apiRequest('/users/profile');
    return response.user;
  },

  updateProfile: async (data: { firstName?: string; lastName?: string; username?: string; email?: string }) => {
    const response = await apiRequest('/users/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.user;
  },
};

// Exchange API
export const exchangeApi = {
  getExchanges: async () => {
    const response = await apiRequest('/exchanges');
    return {
      exchanges: response.exchanges.map((ex: any) => ({
        id: ex.id,
        code: ex.code,
        name: ex.name,
        nameVi: ex.nameVi,
        icon: ex.icon,
        cashbackInfo: ex.cashbackInfo,
        order: ex.order,
      }))
    };
  },

  getAccounts: async (filters?: { status?: string; exchange?: string }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.exchange) params.append('exchange', filters.exchange);

    const response = await apiRequest(`/exchanges/accounts?${params.toString()}`);
    return response.accounts.map((acc: any) => ({
      id: acc.id,
      userId: acc.userId || '',
      exchange: acc.exchange,
      uid: acc.uid,
      status: acc.status,
      totalRebate: acc.totalRebate || 0,
      transactionVolume: acc.transactionVolume || 0,
      createdAt: acc.createdAt,
    }));
  },

  linkAccount: async (data: {
    exchange: 'binance' | 'bybit' | 'okx' | 'gate';
    uid: string;
  }) => {
    const response = await apiRequest('/exchanges/accounts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.account;
  },

  deleteAccount: async (accountId: string) => {
    return apiRequest(`/exchanges/accounts/${accountId}`, {
      method: 'DELETE',
    });
  },
};

// Rebate API
export const rebateApi = {
  getRebates: async (filters?: { status?: string }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);

    const response = await apiRequest(`/rebates?${params.toString()}`);
    return response.rebates.map((r: any) => ({
      id: r.id,
      userId: r.userId || '',
      exchangeAccountId: r.exchangeAccountId || '',
      exchange: r.exchange,
      amount: r.amount,
      description: r.description,
      status: r.status,
      createdAt: r.createdAt,
    }));
  },

  getStats: async () => {
    const response = await apiRequest('/rebates/stats');
    return response.stats;
  },
};

// Transaction API
export const transactionApi = {
  getTransactions: async () => {
    const response = await apiRequest('/transactions');
    return response.transactions.map((tx: any) => ({
      id: tx.id,
      userId: tx.userId || '',
      type: tx.type,
      amount: tx.amount,
      description: tx.description,
      status: tx.status,
      createdAt: tx.createdAt,
    }));
  },
};

// Withdrawal Networks API
export const withdrawalNetworksApi = {
  getNetworks: async (environment?: 'testnet' | 'mainnet') => {
    const params = new URLSearchParams();
    if (environment) params.append('environment', environment);
    const response = await apiRequest(`/withdrawal-networks?${params.toString()}`);
    return response.networks.map((n: any) => ({
      id: n.id,
      name: n.name,
      nameVi: n.nameVi,
      code: n.code,
      network: n.network,
      environment: n.environment,
      tokenAddress: n.tokenAddress,
      tokenSymbol: n.tokenSymbol,
      fee: n.fee,
      minWithdraw: n.minWithdraw,
      maxWithdraw: n.maxWithdraw,
      icon: n.icon,
      order: n.order,
    }));
  },
};

// All History API
export const allHistoryApi = {
  getHistory: async (page = 1, limit = 5) => {
    const response = await apiRequest(`/all-history?page=${page}&limit=${limit}`);
    return response;
  },
};

// Withdrawal API
export const withdrawalApi = {
  getWithdrawals: async () => {
    const response = await apiRequest('/withdrawals');
    return response.withdrawals.map((w: any) => ({
      id: w.id,
      userId: w.userId || '',
      amount: w.amount,
      network: w.network,
      walletAddress: w.walletAddress,
      status: w.status,
      txHash: w.txHash,
      tokenAddress: w.tokenAddress,
      environment: w.environment,
      claimStatus: w.claimStatus,
      contractAddress: w.contractAddress,
      claimTxHash: w.claimTxHash,
      claimedAt: w.claimedAt,
      createdAt: w.createdAt,
      processedAt: w.processedAt,
    }));
  },

  createWithdrawal: async (data: {
    amount: number;
    networkId: string;
    walletAddress: string;
  }) => {
    const response = await apiRequest('/withdrawals', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.withdrawal;
  },

  // Claim withdrawal APIs
  getClaimableWithdrawals: async () => {
    const response = await apiRequest('/claim-withdrawal/claimable');
    return response.withdrawals;
  },

  checkClaimable: async (withdrawalId: string) => {
    const response = await apiRequest(`/claim-withdrawal/check/${withdrawalId}`);
    return response;
  },

  getContractInfo: async (withdrawalId: string) => {
    const response = await apiRequest(`/claim-withdrawal/contract-info/${withdrawalId}`);
    return response;
  },

  markClaimed: async (withdrawalId: string, claimTxHash: string) => {
    const response = await apiRequest(`/claim-withdrawal/mark-claimed/${withdrawalId}`, {
      method: 'POST',
      body: JSON.stringify({ claimTxHash }),
    });
    return response;
  },
};

// CheckIn API
export const checkInApi = {
  getCheckIns: async () => {
    const response = await apiRequest('/checkins');
    return {
      checkIns: response.checkIns.map((ci: any) => ({
        id: ci.id,
        userId: ci.userId || '',
        date: ci.date,
        points: ci.points,
        day: ci.day,
        createdAt: ci.createdAt,
      }))
    };
  },

  checkIn: async () => {
    const response = await apiRequest('/checkins', {
      method: 'POST',
    });
    return response;
  },

  getDailyRewards: async () => {
    const response = await apiRequest('/checkins/daily-rewards');
    return {
      dailyRewards: response.dailyRewards.map((dr: any) => ({
        day: dr.day,
        points: dr.points,
        claimed: dr.claimed,
      }))
    };
  },
};

// Reward API
export const rewardApi = {
  getRewards: async () => {
    const response = await apiRequest('/rewards');
    return {
      rewards: response.rewards.map((r: any) => ({
        id: r.id,
        name: r.name,
        nameVi: r.nameVi,
        description: r.description,
        descriptionVi: r.descriptionVi,
        pointsRequired: r.pointsRequired,
        available: r.available,
        stock: r.stock,
        image: r.image,
        customCode: r.customCode,
      }))
    };
  },

  redeemReward: async (rewardId: string) => {
    const response = await apiRequest(`/rewards/redeem/${rewardId}`, {
      method: 'POST',
    });
    return response;
  },

  getRedeemedRewards: async () => {
    const response = await apiRequest('/rewards/redeemed');
    return response.redeemedRewards.map((rr: any) => ({
      id: rr.id,
      rewardName: rr.rewardName,
      rewardNameVi: rr.rewardNameVi,
      pointsSpent: rr.pointsSpent,
      code: rr.code,
      status: rr.status,
      redeemedAt: rr.redeemedAt,
    }));
  },
};

// Task API
export const taskApi = {
  getTasks: async (category?: string, limit?: number, skip?: number) => {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (limit) params.append('limit', limit.toString());
    if (skip) params.append('skip', skip.toString());

    const response = await apiRequest(`/tasks?${params.toString()}`);
    return {
      tasks: response.tasks.map((t: any) => ({
        id: t.id,
        title: t.title,
        titleVi: t.titleVi,
        description: t.description,
        descriptionVi: t.descriptionVi,
        category: t.category,
        exchange: t.exchange,
        points: t.points,
        icon: t.icon,
        actionType: t.actionType,
        actionUrl: t.actionUrl,
        delayMinutes: t.delayMinutes,
        tradingVolume: t.tradingVolume,
        maxCompletions: t.maxCompletions,
        completionCount: t.completionCount,
        progress: t.progress,
        status: t.status,
        canClaimAt: t.canClaimAt,
        startedAt: t.startedAt,
        createdAt: t.createdAt,
      })),
      hasMore: response.hasMore || false,
      total: response.total || 0
    };
  },

  claimTask: async (taskId: string) => {
    const response = await apiRequest(`/tasks/claim/${taskId}`, {
      method: 'POST',
    });
    return response;
  },
};

// Points History API
export const pointsHistoryApi = {
  getPointsHistory: async () => {
    const response = await apiRequest('/points-history');
    return {
      pointsHistory: response.history.map((h: any) => ({
        id: h.id,
        userId: h.userId || '',
        points: h.points,
        type: h.type,
        description: h.description,
        createdAt: h.createdAt,
      }))
    };
  },
};

// Rebate Rules API
export const rebateRulesApi = {
  getRules: async () => {
    const response = await apiRequest('/rebate-rules');
    return response.rules || [];
  },
};

// Leaderboard API
export const leaderboardApi = {
  getWeeklyLeaderboard: async () => {
    const response = await apiRequest('/leaderboard/weekly');
    return response;
  },
};
